import React from 'react';
import { getEmbedUrl } from '../../utils/media';
import { getStatusBadgeClass, getStatusEmoji } from '../../utils/statusHelpers';
import { cleanDescriptionHtml } from '../../utils/cleanHtml';

// Full-screen modal that renders a document's description, PDF, and/or
// video side-by-side with optional signature/acknowledgement panel.
// Controls (sign toggle, fullscreen, hide document/video, status badge,
// close) live in the top bar. PDF-specific zoom/filter/print/download
// toolbar only appears for mixed PDF+video docs.
//
// State that belongs to this modal (zoom, error retry, which panes are
// hidden, signer name) is lifted to the parent so the parent can clear
// it on close and reuse it across multiple doc openings.
export default function DocumentViewer({
  viewingDocument,
  onClose,
  viewerRef,
  pdfFinalUrl,
  pdfZoomLevel,
  setPdfZoomLevel,
  pdfErrorCount,
  isPdfFilterEnabled,
  setIsPdfFilterEnabled,
  isSigning,
  setIsSigning,
  ackName,
  setAckName,
  hideDescription,
  setHideDescription,
  hideDocument,
  setHideDocument,
  hideVideo,
  setHideVideo,
  descPercent,
  startDrag,
  handlePrintPdf,
  handleDownloadPdf,
  handleSaveSignature,
  handleDeleteAcknowledgement,
  adminMode,
}) {
  if (!viewingDocument) return null;

  // Top-level availability flags for header buttons.
  const fileUrlTop = viewingDocument.fileUrl || viewingDocument.filename || '';
  const embedUrlTop = viewingDocument.videoUrl ? getEmbedUrl(viewingDocument.videoUrl) : null;
  const isDirectVideoTop =
    (viewingDocument.fileType && viewingDocument.fileType.startsWith('video')) ||
    /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(fileUrlTop) ||
    /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(viewingDocument.videoUrl || '');
  const isPdfTop =
    (viewingDocument.fileType && viewingDocument.fileType.includes('pdf')) ||
    /\.pdf(\?|$)/i.test(fileUrlTop) ||
    Boolean(pdfFinalUrl && !isDirectVideoTop && !embedUrlTop);

  // Content-pane flags (recomputed because `isPdfTop` short-circuits
  // via pdfFinalUrl, but the pane layout needs the raw "is this file a
  // PDF?" signal).
  const fileUrl = viewingDocument.fileUrl || viewingDocument.filename || '';
  const isDirectVideo =
    (viewingDocument.fileType && viewingDocument.fileType.startsWith('video')) ||
    /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(fileUrl) ||
    /\.(mp4|webm|ogg|mov|m4v)(\?|$)/i.test(viewingDocument.videoUrl || '');
  const embedUrl = viewingDocument.videoUrl ? getEmbedUrl(viewingDocument.videoUrl) : null;
  const isPdf = (viewingDocument.fileType && viewingDocument.fileType.includes('pdf')) || /\.pdf(\?|$)/i.test(fileUrl);
  const hasMedia = isPdf || embedUrl || isDirectVideo || (pdfFinalUrl && !isDirectVideo && !embedUrl);
  const descHtml = cleanDescriptionHtml(viewingDocument.description || viewingDocument.descriptionHtml || viewingDocument.desc || '');
  // Strip HTML tags before checking — contenteditable can leave behind
  // empty wrappers like <p><br></p> that trim() won't catch.
  const hasDescription = String(descHtml).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim().length > 0;

  // Only surface the pane-toggle group when there are at least two
  // pane types available — a single-media doc has nothing to swap to.
  const hasVideoAvailable = !!(embedUrlTop || isDirectVideoTop);
  const paneCount = (hasDescription ? 1 : 0) + (isPdfTop ? 1 : 0) + (hasVideoAvailable ? 1 : 0);
  const showPaneToggles = paneCount >= 2;

  const handleFullscreen = (e) => {
    e.stopPropagation();
    if (!viewerRef.current) return;
    if (!document.fullscreenElement) viewerRef.current.requestFullscreen().catch(() => {});
    else document.exitFullscreen().catch(() => {});
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90]">
      <div ref={viewerRef} className="bg-white text-black rounded-xl shadow-lg p-6 w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{viewingDocument.name}</h2>
          <div className="flex items-center gap-2">
            {viewingDocument.status && (
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${getStatusBadgeClass(viewingDocument.status)}`}
                title={`Status: ${viewingDocument.status}`}
              >
                <span aria-hidden="true">{getStatusEmoji(viewingDocument.status)}</span>
                <span>{viewingDocument.status}</span>
              </span>
            )}
            <button
              title="Toggle full screen"
              onClick={handleFullscreen}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
              aria-label="Toggle full screen"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" viewBox="0 0 20 20" fill="currentColor"><path d="M3 4a1 1 0 011-1h3a1 1 0 010 2H5v2a1 1 0 11-2 0V4zm14 0v3a1 1 0 11-2 0V5h-2a1 1 0 110-2h3a1 1 0 011 1zM4 16v-3a1 1 0 112 0v2h2a1 1 0 110 2H5a1 1 0 01-1-1zm12 0a1 1 0 01-1 1h-3a1 1 0 110-2h2v-2a1 1 0 112 0v3z"/></svg>
            </button>
            {/* Segmented pane picker. Hidden when only one pane type
                exists (nothing to swap to). Description toggles
                independently (checkbox-style); Document and Video are
                radio-like — showing one auto-hides the other. */}
            {showPaneToggles && (
              <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 overflow-hidden text-xs font-semibold" role="group" aria-label="Toggle viewer panes">
                {hasDescription && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setHideDescription(prev => !prev); }}
                    className={`px-3 py-1.5 transition-colors border-r border-gray-200 ${
                      !hideDescription
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                    aria-pressed={!hideDescription}
                    title={!hideDescription ? 'Hide description' : 'Show description'}
                  >
                    📝 Description
                  </button>
                )}
                {isPdfTop && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Show document → hide video. Hiding document is
                      // only allowed when a video is available to switch
                      // to, otherwise clicking a hidden button re-shows it.
                      if (hideDocument) {
                        setHideDocument(false);
                        setHideVideo(true);
                      } else if (hasVideoAvailable) {
                        setHideDocument(true);
                        setHideVideo(false);
                      }
                    }}
                    className={`px-3 py-1.5 transition-colors border-r border-gray-200 ${
                      !hideDocument
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                    aria-pressed={!hideDocument}
                    title={!hideDocument ? 'Currently viewing document' : 'Switch to document'}
                  >
                    📄 Document
                  </button>
                )}
                {hasVideoAvailable && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (hideVideo) {
                        setHideVideo(false);
                        if (isPdfTop) setHideDocument(true);
                      } else if (isPdfTop) {
                        setHideVideo(true);
                        setHideDocument(false);
                      }
                    }}
                    className={`px-3 py-1.5 transition-colors ${
                      !hideVideo
                        ? 'bg-teal-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                    }`}
                    aria-pressed={!hideVideo}
                    title={!hideVideo ? 'Currently viewing video' : 'Switch to video'}
                  >
                    🎥 Video
                  </button>
                )}
              </div>
            )}
            {viewingDocument.signatureRequired && (
              <button
                title={isSigning ? 'Hide signature panel' : 'Sign this document'}
                onClick={(e) => { e.stopPropagation(); setIsSigning(prev => !prev); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  isSigning
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100'
                }`}
                aria-pressed={isSigning}
                aria-label="Toggle signing panel"
              >
                ✍️ Sign
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors"
              aria-label="Close viewer"
              title="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        {/* PDF+video mixed-media toolbar (only shown when both present) */}
        {viewingDocument.filename && viewingDocument.videoUrl && (
          <div className="flex justify-center mb-2 border-b">
            <button
              onClick={() => setIsPdfFilterEnabled(!isPdfFilterEnabled)}
              className={`p-1.5 rounded-full hover:bg-gray-200 transition-colors ${isPdfFilterEnabled ? 'bg-blue-100' : ''}`}
              aria-label="Toggle PDF Filter"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.022 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
            </button>
            <button onClick={() => setPdfZoomLevel(prev => Math.max(50, prev - 10))} className="p-1.5 rounded-full hover:bg-gray-200 transition-colors" aria-label="Zoom Out">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" /></svg>
            </button>
            <span className="min-w-[40px] text-center">{pdfZoomLevel}%</span>
            <button onClick={() => setPdfZoomLevel(prev => Math.min(200, prev + 10))} className="p-1.5 rounded-full hover:bg-gray-200 transition-colors" aria-label="Zoom In">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3h-6" /></svg>
            </button>
            <button onClick={handleDownloadPdf} className="p-1.5 rounded-full hover:bg-gray-200 transition-colors ml-4" aria-label="Download">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
            </button>
            <button title="Print" onClick={handlePrintPdf} className="p-1.5 rounded-full hover:bg-gray-200 transition-colors" aria-label="Print">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm7-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
            </button>
          </div>
        )}

        {/* Content + optional signature panel */}
        <div className="flex-grow flex gap-4 overflow-auto">
          <div className="flex-grow h-full">
            {hasDescription && !hasMedia ? (
              // Description-only layout: centered card.
              <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-gray-50 overflow-hidden">
                <div className="bg-white p-8 rounded-xl shadow-md max-w-3xl w-full max-h-full overflow-y-auto border border-gray-100">
                  <h3 className="text-xl font-bold mb-4 text-center border-b pb-2 text-teal-800">{viewingDocument.name}</h3>
                  <div className="prose prose-lg max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: descHtml }} />
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex flex-col min-h-0">
                {/* Description is fixed-basis at top (clamped 5–70% via
                    descPercent), media pane uses flex-1 + min-h-0 to
                    fill remaining height. */}
                {!hideDescription && hasDescription && (
                  <div
                    className="p-4 text-black overflow-auto flex-shrink-0"
                    style={{ flexBasis: `${descPercent}%`, minHeight: 60, maxHeight: '70%' }}
                  >
                    <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: descHtml }} />
                  </div>
                )}

                {!hideDescription && hasDescription && (!hideDocument || !hideVideo) && (
                  <div
                    onMouseDown={(e) => startDrag('descPdf', e)}
                    className="w-full cursor-row-resize flex-shrink-0"
                    style={{ height: 8, background: 'linear-gradient(90deg,#fff0,#e5e7eb,#fff0)' }}
                    title="Drag to resize description"
                  />
                )}

                {!hideDocument && (isPdf || (pdfFinalUrl && !isDirectVideo && !embedUrl)) && (
                  <div className="flex-1 min-h-0 overflow-hidden border-t">
                    <iframe
                      key={(pdfFinalUrl || viewingDocument.fileUrl || viewingDocument.id) + '-' + pdfErrorCount}
                      src={pdfFinalUrl || viewingDocument.fileUrl}
                      className="w-full h-full"
                      title="PDF Viewer"
                    />
                  </div>
                )}

                {!hideVideo && (embedUrl || isDirectVideo) && (
                  <div className="flex-1 min-h-0 overflow-hidden border-t bg-black">
                    {embedUrl ? (
                      <iframe
                        src={embedUrl}
                        className="w-full h-full"
                        title="Embedded Video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        controls
                        className="w-full h-full"
                        style={{ backgroundColor: 'black' }}
                        src={viewingDocument.fileUrl || viewingDocument.videoUrl}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {isSigning && (
            <div className="w-1/3 h-full flex flex-col border-l pl-4 bg-gray-50 pt-4">
              <h4 className="font-bold text-lg mb-4 text-gray-800">Acknowledge Document</h4>

              <div className="bg-white p-6 rounded-xl border shadow-sm mb-6">
                <div className="mb-4 flex items-start gap-3 bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <div className="min-w-[20px] pt-0.5 text-blue-600">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </div>
                  <p className="text-sm text-gray-700 leading-snug">
                    I verify that I have read and understand the document <strong>"{viewingDocument.name}"</strong>.
                  </p>
                </div>

                <div className="space-y-3">
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider">Your Full Name</label>
                  <input
                    type="text"
                    placeholder="Type name here..."
                    className="border p-3 rounded-lg w-full focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                    value={ackName}
                    onChange={(e) => setAckName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSaveSignature(viewingDocument.id); }}
                    autoFocus
                  />
                  <button
                    title="Sign document"
                    className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-3 rounded-lg font-bold shadow-md hover:shadow-lg transition-all w-full flex items-center justify-center gap-2 mt-2"
                    onClick={() => handleSaveSignature(viewingDocument.id)}
                  >
                    <span>Confirm & Sign</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 my-2" />

              <div className="flex-grow border rounded-lg p-2 overflow-y-auto mb-3 bg-white">
                <h4 className="font-semibold mb-2 text-sm text-gray-500 uppercase px-2 pt-2">History</h4>
                <ul className="space-y-2">
                  {(viewingDocument.acknowledgements || []).map(ack => (
                    <li key={ack.id} className="flex justify-between items-center text-sm bg-gray-100 p-2 rounded-md">
                      <div className="flex items-center gap-3 ">
                        <strong>{ack.name}</strong>
                        <span className="text-xs text-gray-600">{new Date(ack.timestamp).toLocaleString()}</span>
                        {ack.signature && (
                          <img src={ack.signature} alt="signature" className="h-8 ml-2 rounded-sm border" />
                        )}
                      </div>
                      {adminMode && (
                        <button
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteAcknowledgement(viewingDocument.id, ack.id)}
                        >
                          &times;
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
